// ./src/utils/api/VolunteerSlotService.ts
//
// VolunteerSlot (event-scoped volunteering needs) service. OpResult-native.
// Account-owned; each slot belongs to an event. Coupon rewards are edited in one
// shot via the nested `rewards` input (server diffs on reward id). A multi-id
// update/remove is a bulk op (server restricts a bulk update to status).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants (mirror the eventsmagic-api VOLUNTEER_* boundary)
// =============================================================================

export const VOLUNTEER_LOCATION_KIND_VALUES = ["venue", "poi", "address", "coordinates"] as const;
export type VolunteerLocationKind = (typeof VOLUNTEER_LOCATION_KIND_VALUES)[number];

export const VOLUNTEER_RADIUS_UNIT_VALUES = ["m", "ft"] as const;

/**
 * How a slot takes PUBLIC sign-ups (#A4.6) — mirrors `VOLUNTEER_APPROVAL_MODE` in eventsmagic-api.
 *   review — a sign-up waits for the host; the place is taken on approval.
 *   auto   — the sign-up is confirmed on the spot, while places last.
 */
export const VOLUNTEER_APPROVAL_MODE = {
  REVIEW: "review",
  AUTO: "auto",
} as const;
export type VolunteerRadiusUnit = (typeof VOLUNTEER_RADIUS_UNIT_VALUES)[number];

// =============================================================================
// GraphQL
// =============================================================================

const VOLUNTEER_SLOT_FIELDS = `
  id
  accountId
  eventId
  volunteerRoleId
  name
  instructions
  quantityNeeded
  startsAt
  endsAt
  locationKind
  locationVenueId
  pointOfInterestId
  streetAddress
  latitude
  longitude
  radius
  radiusUnit
  locationLabel
  orderNo
  status
  rights
  assignedCount
  isOpenForApplications
  approvalMode
  applicationsCloseAt
  hourlyRate
  pendingRequestCount
  createdAt
  updatedAt
  rewards {
    id
    couponTypeId
    quantity
    orderNo
  }
`;

const VOLUNTEER_SLOT_LIST_QUERY = gql`
  query VolunteerSlotList($filter: EventVolunteerSlotFilter, $params: PaginationAndSorting) {
    eventVolunteerSlotList(filter: $filter, params: $params) {
      code
      data { ${VOLUNTEER_SLOT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VOLUNTEER_SLOT_GET_QUERY = gql`
  query VolunteerSlotGet($id: ID) {
    eventVolunteerSlotGet(id: $id) {
      code
      data { ${VOLUNTEER_SLOT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VOLUNTEER_SLOT_GET_MANY_QUERY = gql`
  query VolunteerSlotGetMany($ids: [ID]) {
    eventVolunteerSlotGetMany(ids: $ids) {
      code
      data { ${VOLUNTEER_SLOT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VOLUNTEER_SLOT_CREATE_MUTATION = gql`
  mutation VolunteerSlotCreate($params: EventVolunteerSlotInput) {
    eventVolunteerSlotCreate(params: $params) {
      code
      data { ${VOLUNTEER_SLOT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VOLUNTEER_SLOT_UPDATE_MUTATION = gql`
  mutation VolunteerSlotUpdate($where: EventVolunteerSlotWhereInput, $params: EventVolunteerSlotInput) {
    eventVolunteerSlotUpdate(where: $where, params: $params) {
      code
      data { ${VOLUNTEER_SLOT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VOLUNTEER_SLOT_REMOVE_MUTATION = gql`
  mutation VolunteerSlotRemove($where: EventVolunteerSlotWhereInput) {
    eventVolunteerSlotRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface VolunteerSlotReward {
  id?: string;
  couponTypeId?: string | null;
  quantity?: number | null;
  orderNo?: number | null;
}

export interface VolunteerSlot {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  volunteerRoleId?: string | null;
  name?: string | null;
  instructions?: string | null;
  quantityNeeded?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  locationKind?: string | null;
  locationVenueId?: string | null;
  pointOfInterestId?: string | null;
  streetAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radius?: number | null;
  radiusUnit?: string | null;
  locationLabel?: string | null;
  orderNo?: number;
  status?: number;
  rights?: string[] | null;
  /** How many crew are currently assigned (active) to this slot (server-resolved). */
  assignedCount?: number | null;
  /** Whether the public may sign up for this shift (#A4.6). */
  isOpenForApplications?: boolean | null;
  /** review | auto — whether a public sign-up waits for the host or is confirmed on the spot. */
  approvalMode?: string | null;
  /** Sign-ups shut after this instant (ISO UTC). Null = open until the shift. */
  applicationsCloseAt?: string | null;
  /** #A4.7 — overrides the role's hourly rate for this shift (event currency); null = the role's. */
  hourlyRate?: number | null;
  /** Requested shifts on volunteer applications still waiting for a decision (server-resolved). */
  pendingRequestCount?: number | null;
  rewards?: VolunteerSlotReward[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface VolunteerSlotRewardInput {
  id?: string;
  couponTypeId?: string | null;
  quantity?: number | null;
}

export interface VolunteerSlotInput {
  eventId?: string | null;
  volunteerRoleId?: string | null;
  name?: string | null;
  instructions?: string | null;
  quantityNeeded?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  locationKind?: string | null;
  locationVenueId?: string | null;
  pointOfInterestId?: string | null;
  streetAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radius?: number | null;
  radiusUnit?: string | null;
  locationLabel?: string | null;
  orderNo?: number | null;
  status?: number | null;
  /** ⚠ REQUIRED on create (server-refused when omitted) — never decided by a default. */
  isOpenForApplications?: boolean | null;
  /** ⚠ REQUIRED on create — review | auto. */
  approvalMode?: string | null;
  applicationsCloseAt?: string | null;
  /** #A4.7 — null = the role's rate. ⚠ Send it ONLY when the caller may manage the budget: crew without
   *  budget.edit are refused for the field being present at all. */
  hourlyRate?: number | null;
  rewards?: VolunteerSlotRewardInput[] | null;
}

// A bulk update (>1 id) may only mass-change status and the sign-up settings (server-enforced).
export interface VolunteerSlotBulkInput {
  status?: number | null;
  isOpenForApplications?: boolean | null;
  approvalMode?: string | null;
}

export interface VolunteerSlotFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  volunteerRoleId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface VolunteerSlotWhereInput {
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class VolunteerSlotService {
  static async list(
    filter?: VolunteerSlotFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(VOLUNTEER_SLOT_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more button,
   * so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: VolunteerSlotFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<VolunteerSlot>(VolunteerSlotService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(VOLUNTEER_SLOT_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(VOLUNTEER_SLOT_GET_MANY_QUERY, { ids });
  }

  static async create(params: VolunteerSlotInput): Promise<OpResult> {
    return ApiService.executeMutation(VOLUNTEER_SLOT_CREATE_MUTATION, { params });
  }

  static async update(where: VolunteerSlotWhereInput, params: VolunteerSlotInput): Promise<OpResult> {
    return ApiService.executeMutation(VOLUNTEER_SLOT_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(VOLUNTEER_SLOT_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status on every selected slot in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: VolunteerSlotBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(VOLUNTEER_SLOT_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected slot in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(VOLUNTEER_SLOT_REMOVE_MUTATION, { where: { id: ids } });
  }
}
